import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Intercom } from '@supy-io/ngx-intercom';
import { DateTime } from 'luxon';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { UserLiteDTO } from 'src/app/common/state/user/user.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { booleanFromBooleanNumber } from 'src/app/common/utilities/enums/boolean-number.enum';
import { ModalComponent } from 'src/app/private/shared/components/modals/modal/modal.component';
import { ProformSurveyModalComponent } from 'src/app/private/shared/components/modals/proform-survey-modal/proform-survey-modal.component';
import {
  PdAttendee,
  PdContext,
  PdCreditAwardingMethod,
  PdCreditsReceived,
  PdEarnedItem,
  PdFormUser,
  PdSession,
  PdSessionAPIResponse,
  PdSessionFinishedPayload,
  PdSessionFinishedUserPayload,
  PdSessionFinishedUserPayloadFromPdFormUser,
  PdViewingUser,
} from 'src/app/private/shared/dtos/pd.dto';
import { ProformViewStates } from 'src/app/private/shared/dtos/proforms.dto';
import {
  AlertService,
  AlertType,
} from 'src/app/private/shared/services/alert/alert.service';
import { LMSCourseService } from 'src/app/private/shared/services/lms-courses/lms-course.service';
import { PdPusherService } from 'src/app/private/shared/services/pd/pd-pusher.service';
import { PdService } from 'src/app/private/shared/services/pd/pd.service';
import {
  ProFormContext,
  ProFormsSubmissionAPIResponse,
} from 'src/app/private/shared/types/responses/proforms.responses';
import { ProformsSubmissionService } from '../../../shared/services/proforms/proforms-submission.service';

@Component({
  selector: 'app-microlearning-session-details',
  templateUrl: './microlearning-session-details.component.html',
  styleUrl: './microlearning-session-details.component.scss',
  standalone: false,
})
export class MicrolearningSessionDetailsComponent implements OnInit, OnDestroy {
  sessionId: number;

  selAttendeeId: number;

  selAttendee: PdAttendee | undefined;

  session: PdSession;

  context: PdContext;

  user: User;

  isRegistered: boolean;

  isFacilitator: boolean;

  isCreator: boolean;

  backgroundImage: string;

  public API_URL = EnvironmentService.apiUrl();

  earnedCredits: PdEarnedItem[];

  errorMessage: string | undefined;

  // NOTES
  initialNotesContent: string;

  isSavingNotes = false;

  creditsReceived: PdCreditsReceived;

  viewingUser: PdViewingUser;

  // ATTENDEE MGMT FORM
  usersCreditForm: FormGroup;

  usersCreditFormSubmitted = false;

  userPayload: PdSessionFinishedUserPayload[];

  formSubmitting = false;

  isInRegistrationWindow = false;

  // SURVEY
  @ViewChild('surveyModal')
  surveyModal: ProformSurveyModalComponent;

  @ViewChild('surveySubmissionModal')
  surveySubmissionModal: ModalComponent;

  surveySubmissionsForSession: ProFormsSubmissionAPIResponse[];

  attendeeSurveySubmission: ProFormsSubmissionAPIResponse;

  attendeeSurveySubmissionId: number;

  @ViewChild('quizSubmissionModal')
  quizSubmissionModal: ModalComponent;

  quizSubmissionsForSession: ProFormsSubmissionAPIResponse[];

  attendeeQuizSubmission: ProFormsSubmissionAPIResponse;

  attendeeQuizSubmissionId: number;

  viewStates = ProformViewStates;

  forbidden = false;

  registerLoading = false;

  PdCreditAwardingMethod = PdCreditAwardingMethod;

  constructor(
    private pdService: PdService,
    private route: ActivatedRoute,
    public router: Router,
    private store: Store,
    private clipboard: Clipboard,
    private fb: FormBuilder,
    private alertService: AlertService,
    private pdPusherService: PdPusherService,
    private cdr: ChangeDetectorRef,
    private intercom: Intercom,
    private lmsCourseService: LMSCourseService,
    private proformsSubmissionService: ProformsSubmissionService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.sessionId = parseInt(params['sessionId']);
      this.selAttendeeId = this.user.id;

      this.getSession(this.sessionId);
    });

    // Pusher Event Listeners:
    // Session details
    this.pdPusherService.sessionUpdatedNotifier.subscribe(() => {
      this.getSession(this.sessionId);
    });

    this.pdPusherService.sessionDeletedNotifier.subscribe(() => {
      this.navigateToCatalog();
    });

    // Attendee added
    this.pdPusherService.attendeeAddedNotifier.subscribe(() => {
      this.getSession(this.sessionId);
    });

    // Attendee deleted
    this.pdPusherService.attendeeDeletedNotifier.subscribe(() => {
      this.getSession(this.sessionId);
    });
  }

  previewCourse() {
    window.open(this.session.course?.preview_link, '_blank');
  }

  // eslint-disable-next-line class-methods-use-this
  creditValueValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const creditValue = control.get('credit_value')?.value;
      const maxValue = control.get('credit_max_value')?.value;

      const errors: ValidationErrors = {};

      // Check for missing credit fields (allowing 0 for credit_value)
      if (
        creditValue === null ||
        creditValue === undefined ||
        maxValue === null ||
        maxValue === undefined
      ) {
        errors['required'] = true;
      }

      // Check if credit value exceeds max value
      if (
        creditValue !== undefined &&
        creditValue !== null &&
        maxValue !== undefined &&
        maxValue !== null &&
        creditValue > maxValue
      ) {
        errors['maxValueExceeded'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  private buildForm() {
    this.usersCreditForm = this.fb.group({
      users: this.fb.array([]),
    });

    if (this.session.attendees && this.session.attendees.length > 0) {
      const usersArray = this.usersCreditForm.controls['users'] as FormArray;

      this.session.attendees?.forEach((attendee) => {
        const newUserGroup = new FormGroup({
          user_id: new FormControl(attendee.user_id, []),
          user_profile: new FormControl(attendee.user.profile),
          is_present: new FormControl(
            booleanFromBooleanNumber(attendee.is_present),
            []
          ),
          credits: this.fb.array([]),
        });

        if (this.session.credits && this.session.credits.length > 0) {
          const creditsArray = newUserGroup.controls.credits as FormArray;
          this.session.credits.forEach((credit) => {
            const newCreditGroupType = new FormGroup(
              {
                credit_type: new FormControl(
                  credit.credit_type,
                  Validators.required
                ),
                credit_value: new FormControl(
                  credit.credit_amount,
                  Validators.required
                ),
                credit_max_value: new FormControl(credit.credit_amount),
              },
              { validators: this.creditValueValidator() }
            );
            creditsArray.push(newCreditGroupType);
          });
        }

        usersArray.push(newUserGroup);
      });
    }
  }

  get users() {
    return this.usersCreditForm.get('users') as FormArray;
  }

  getCreditsFor(index: number) {
    return (<FormArray>(
      (<FormArray>this.usersCreditForm.get('users')).controls[index].get(
        'credits'
      )
    )).controls;
  }

  navigateToCatalog() {
    this.router.navigate(['/training/catalog']);
  }

  getCompletedSession(sessionId: number, selUserId: number) {
    this.pdService
      .getCompletedUserSession(sessionId, selUserId)
      .subscribe((resp: PdSessionAPIResponse) => {
        if (this.session?.is_completed) {
          this.viewingUser = resp.context.viewing_user;
          if (this.session.attendees) {
            this.selAttendee = this.session.attendees?.find(
              (user) => user.user_id === this.selAttendeeId
            );
          }
          this.creditsReceived = resp.context.credits_received;
        }
      });
  }

  getSession(sessionId: number) {
    this.pdService.getSession(sessionId).subscribe({
      error: (error) => {
        if (error.status === 403) {
          this.forbidden = true;
        }
      },
      next: (resp: PdSessionAPIResponse) => {
        if (resp.item) {
          this.session = resp.item;
          this.context = resp.context;
          this.initialNotesContent = this.session.notes;

          this.isInRegistrationWindow =
            this.session.registration_start_datetime <=
              DateTime.now().toSeconds() &&
            this.session.registration_end_datetime >=
              DateTime.now().toSeconds();

          this.getEarnedCredits(this.session.id);
          this.creditsReceived = this.context.credits_received;

          if (this.session.is_completed) {
            document.body.classList.add('session_post');
            if (this.selAttendeeId) {
              this.getCompletedSession(this.sessionId, this.selAttendeeId);
            } else {
              this.viewingUser = resp.context.viewing_user;
            }
          } else {
            document.body.classList.add('session_pre');
          }

          if (this.session.image) {
            this.backgroundImage = `url(${this.API_URL}${this.session.image})`;
          } else {
            this.backgroundImage = 'url(/assets/pd_list_item_default.png)';
          }

          if (this.user) {
            // IS USER A FACILITATOR?
            if (
              this.session.facilitators &&
              this.session.facilitators.length > 0
            ) {
              this.isFacilitator = this.session.facilitators.some(
                (facilitator) =>
                  facilitator.facilitator_user_id === this.user.id
              );

              this.isCreator = this.session.creator_user_id === this.user.id;

              if (this.isFacilitator) {
                this.buildForm();
              }

              if (this.session.attendees && this.session.attendees.length > 0) {
                // IF NOT A FACILITATOR, CHECK ATTENDEE CREDENTIALS
                if (!this.isFacilitator) {
                  // IS USER REGISTERED?
                  this.isRegistered = this.session.attendees.some(
                    (attendee) => attendee.user_id === this.user?.id
                  );
                }
              } else {
                this.isRegistered = false;
              }
            }
          }

          if (this.session.quiz?.submissions) {
            this.quizSubmissionsForSession =
              this.session.quiz.submissions.filter(
                (submission) =>
                  submission.context_table_id === this.session.id &&
                  submission.context_table === ProFormContext.PD_SESSION
              );
            if (!this.isFacilitator) {
              const attendeeQuizSubmission =
                this.quizSubmissionsForSession.find(
                  (submission) =>
                    submission.user_id === this.user.id &&
                    submission.context_table_id === this.session.id &&
                    submission.context_table === ProFormContext.PD_SESSION
                );
              if (attendeeQuizSubmission) {
                this.attendeeQuizSubmission = attendeeQuizSubmission;
              }
            }
          }

          if (this.session.survey?.submissions) {
            this.surveySubmissionsForSession =
              this.session.survey.submissions.filter(
                (submission) =>
                  submission.context_table_id === this.session.id &&
                  submission.context_table === ProFormContext.PD_SESSION
              );
            if (!this.isFacilitator) {
              const attendeeSurveySubmission =
                this.surveySubmissionsForSession.find(
                  (submission) =>
                    submission.user_id === this.user.id &&
                    submission.context_table_id === this.session.id &&
                    submission.context_table === ProFormContext.PD_SESSION
                );
              if (attendeeSurveySubmission) {
                this.attendeeSurveySubmission = attendeeSurveySubmission;
              }
            }
          }
        }
      },
    });
  }

  unregisterUser(sessionId: number, userId: number) {
    this.pdService.unregisterSession(sessionId, userId).subscribe(() => {
      if (this.isFacilitator) {
        this.getSession(sessionId);
      } else {
        this.isRegistered = false;
        this.getSession(sessionId);
      }
    });
  }

  // BEGIN ATTENDEE ONLY FEATURES
  selfRegister(sessionId: number) {
    this.registerLoading = true;
    this.pdService.registerSession(sessionId).subscribe({
      error: (error) => {
        this.alertService.showAlert(
          error.error.message,
          undefined,
          AlertType.DANGER
        );
        this.registerLoading = true;
      },
      next: (res) => {
        const metadata = {
          pd_session_id: res.pd_session_id,
          user_id: res.user_id,
          district_id: res.user.district_id,
        };
        this.intercom.trackEvent('Microlearning Self Registration', metadata);
        if (this.session.lms_course_id) {
          this.lmsCourseService
            .createCourseRegistration(this.session.lms_course_id, this.user.id)
            .subscribe((resp) => {
              if (resp) {
                if (resp.error) {
                  this.alertService.showAlert(
                    resp.error,
                    undefined,
                    AlertType.DANGER
                  );
                } else {
                  this.getSession(sessionId);
                }
              }
              this.registerLoading = false;
            });
        } else {
          this.getSession(sessionId);
          this.registerLoading = false;
        }
      },
    });
  }

  takeSurvey() {
    this.surveyModal.openModal();
  }
  // END ATTENDEE ONLY FEATURES

  // BEGIN FACILITATOR ONLY FEATURES
  copyCode(code: string) {
    this.clipboard.copy(code);
    this.alertService.showAlert('Attendence Code copied to clipboard');
  }

  updateSession(session: PdSession) {
    this.getSession(session.id);
  }

  updateNotes(newNotes: string) {
    this.isSavingNotes = true;

    const shouldSave = this.session.lms_course_id || newNotes.trim().length > 0;

    if (this.session.notes !== newNotes && shouldSave) {
      this.pdService
        .updateSession(this.session.id, { notes: newNotes })
        .subscribe((resp) => {
          if (resp) {
            setTimeout(() => {
              this.isSavingNotes = false;
              this.cdr.detectChanges();
            }, 500);
          }
        });
    } else {
      this.isSavingNotes = false;
    }
  }

  // ATTENDEE MANAGEMENT
  registerUser(newAttendee: UserLiteDTO | null) {
    if (newAttendee) {
      this.pdService
        .registerSessionForAnotherUser(this.sessionId, newAttendee.id)
        .subscribe({
          error: (error) => {
            this.alertService.showAlert(
              error.error.message,
              undefined,
              AlertType.DANGER
            );
          },
          next: (res: PdAttendee) => {
            const metadata = {
              pd_session_id: res.pd_session_id,
              user_id: res.user_id,
              district_id: res.user.district_id,
            };
            this.intercom.trackEvent('PD Facilitator Registration', metadata);
            if (this.session.lms_course_id) {
              this.lmsCourseService
                .createCourseRegistration(
                  this.session.lms_course_id,
                  res.user_id
                )
                .subscribe((resp) => {
                  if (resp) {
                    if (resp.error) {
                      this.alertService.showAlert(
                        resp.error,
                        undefined,
                        AlertType.DANGER
                      );
                    } else {
                      this.getSession(this.sessionId);
                    }
                  }
                });
            }
            this.session.attendees?.push(res);
            this.addUsertoFormGroup(res);
          },
        });
    }
  }

  addUsertoFormGroup(attendee: PdAttendee) {
    const usersArray = this.usersCreditForm.controls['users'] as FormArray;

    const newUserGroup = new FormGroup({
      user_id: new FormControl(attendee.user_id, []),
      user_profile: new FormControl(attendee.user.profile),
      is_present: new FormControl(attendee.is_present, []),
      credits: this.fb.array([]),
    });

    const creditsArray = newUserGroup.controls.credits as FormArray;

    if (this.session.credits) {
      this.session.credits.forEach((credit) => {
        const newCreditGroupType = new FormGroup(
          {
            credit_type: new FormControl(credit.credit_type, [
              Validators.required,
            ]),
            credit_value: new FormControl(credit.credit_amount, [
              Validators.required,
            ]),
            credit_max_value: new FormControl(credit.credit_amount, []),
          },
          { validators: this.creditValueValidator() }
        );
        creditsArray.push(newCreditGroupType);
      });
    }

    usersArray.push(newUserGroup);
  }

  onPresentCheckboxChange($event: Event, attendeeId: number): void {
    const target = $event.target as HTMLInputElement | null;
    if (target) {
      const updatedValue = target.checked ? 1 : 0;
      this.pdService
        .setAttendeePresent(this.session.id, attendeeId, updatedValue)
        .subscribe();
    }
  }

  completeSession() {
    if (this.usersCreditForm.invalid) {
      return;
    }

    this.formSubmitting = true;

    let sessionFormUsers: PdFormUser[] = [];

    this.usersCreditForm.value.users.forEach((user: PdFormUser) => {
      sessionFormUsers.push(user);
    });
    // REMOVE USER IF NOT PRESENT
    sessionFormUsers = sessionFormUsers.filter(
      (user) => user.is_present === true
    );

    // REMOVE UNNECESSARY VALULES
    sessionFormUsers = sessionFormUsers.map((user) =>
      PdSessionFinishedUserPayloadFromPdFormUser(user)
    );

    // CREATE FINAL PAYLOAD
    const sessionCompletePayload: PdSessionFinishedPayload = {
      users: sessionFormUsers,
    };

    this.pdService
      .completeSession(this.sessionId, sessionCompletePayload)
      .subscribe({
        error: () => {
          this.formSubmitting = false;
          this.alertService.showAlert('Session could not be submitted');
        },
        next: () => {
          this.getSession(this.sessionId);
          this.intercom.trackEvent('PD session completed', {
            pd_session_id: this.sessionId,
            user_id: this.user.id,
            district_id: this.session.district_id,
          });
        },
      });
  }

  getEarnedCredits(sessionId: number) {
    this.pdService.sessionEarnedCredits(sessionId).subscribe((resp) => {
      if (resp.items) {
        this.earnedCredits = resp.items;
      }
    });
  }

  onSurveySubmit(submission: ProFormsSubmissionAPIResponse) {
    this.attendeeSurveySubmission = submission;
  }

  getAttendeeSurveySubmission(userId: number) {
    if (this.surveySubmissionsForSession) {
      return this.surveySubmissionsForSession.find(
        (submission) => submission.user_id === userId
      );
    }
    return null;
  }

  openSurveySubmissionModal(userId: number) {
    const attendeeSurveySubmission = this.getAttendeeSurveySubmission(userId);
    if (attendeeSurveySubmission) {
      this.attendeeSurveySubmissionId = attendeeSurveySubmission.id;
      this.proformsSubmissionService.getSubmission(
        this.attendeeSurveySubmissionId
      );
      this.surveySubmissionModal.open();
    }
  }

  getAttendeeQuizSubmission(userId: number) {
    if (this.quizSubmissionsForSession) {
      const submissions = this.quizSubmissionsForSession.filter(
        (submission) => submission.user_id === userId
      );
      return submissions[submissions.length - 1];
    }
    return null;
  }

  openQuizSubmissionModal(userId: number) {
    const attendeeQuizSubmission = this.getAttendeeQuizSubmission(userId);
    if (attendeeQuizSubmission) {
      this.attendeeQuizSubmissionId = attendeeQuizSubmission.id;
      this.proformsSubmissionService.getSubmission(
        this.attendeeQuizSubmissionId
      );
      this.quizSubmissionModal.open();
    }
  }

  hasEarnedCredits(userId: number): boolean {
    return (
      this.earnedCredits?.some(
        (earned) =>
          Number(earned.user.id) === Number(userId) && earned.credits.length > 0
      ) || false
    );
  }

  getEarnedCreditsForUser(userId: number) {
    return (
      this.earnedCredits?.filter(
        (earnedCredit) => Number(earnedCredit.user.id) === Number(userId)
      ) || []
    );
  }

  awardCreditsToUser(userId: number) {
    const usersPayload = this.usersCreditForm.value.users
      .filter((user: PdFormUser) => user.user_id === userId)
      .map((user: PdFormUser) =>
        PdSessionFinishedUserPayloadFromPdFormUser(user)
      );

    this.pdService
      .awardCreditsToUser(this.session.id, { users: usersPayload })
      .subscribe(() => {
        this.getEarnedCredits(this.session.id);
      });
  }

  unawardCreditsFromUser(userId: number) {
    this.pdService
      .unawardCreditsFromUser(this.session.id, userId)
      .subscribe(() => {
        this.getEarnedCredits(this.session.id);
      });
  }

  // END FACILITATOR ONLY FEATURES

  // eslint-disable-next-line class-methods-use-this
  ngOnDestroy() {
    // REMOVE BACKGROUND CLASSES!
    document.body.classList.remove('session_pre');
    document.body.classList.remove('session_post');
  }
}
